import React, { forwardRef, useEffect, useImperativeHandle, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  CircularProgress,
  Checkbox,
  Grid,
  Paper,
  makeStyles
} from "@material-ui/core";

import { searchDataByField } from "../../utils/dataUtils";
import { fetchAssignedCreatives } from "../../actions/dcm/assignedCreatives";
import TableToolbar from "./TableToolbar";
import { useHistory } from "react-router-dom";
import { fetchSizeService } from "../../services/dcm/size";

const useCreativeListStyles = makeStyles((theme) => ({
  grid: {
    marginTop: theme.spacing(2)
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    padding: theme.spacing(2, 4, 3),
    marginBottom: theme.spacing(2)
  }
}));

export default forwardRef(
  (
    { creativeTypes, selectedCreatives, setSelectedCreatives, placements },
    ref
  ) => {
    const classes = useCreativeListStyles();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(25);
    const [searchText, setSearchText] = React.useState("");
    const dispatch = useDispatch();
    const stableDispatch = useCallback((args) => dispatch(args), [dispatch]);
    const isLoading = useSelector(
      (state) => state.dcmAssignedCreatives.isLoading
    );
    let dcmCreatives = useSelector((state) =>
      state.dcmAssignedCreatives.allIds.map(
        (id) => state.dcmAssignedCreatives.byId[id]
      )
    );
    let campaigns = useSelector((state) => state.dcmCampaigns);

    const history = useHistory();
    let routeName = history.location.pathname; //after the split dimensions is the second element

    dcmCreatives = searchDataByField(dcmCreatives, "name", "id", searchText);

    useEffect(() => {
      if (dcmCreatives.length > 0) {
        return; // already loaded, no need to reload
      }

      let sizes = [];
      let advertiserId = "";

      let ignorePlacementDimensions =
        creativeTypes.includes("TRACKING_TEXT") ||
        creativeTypes.includes("INSTREAM_VIDEO");

      if (placements && !ignorePlacementDimensions) {
        for (let placement of placements) {
          sizes.push(placement.size.id);
        }
      }

      if (campaigns.selectedId.length > 0) {
        advertiserId = campaigns.byId[campaigns.selectedId].advertiserId;
      }

      stableDispatch(
        fetchAssignedCreatives("all", creativeTypes, sizes, advertiserId)
      );
    }, []);

    useEffect(() => {
      const getSizeId = async () => {
        let onDeufaultRoute = routeName.split('/default/').length > 1;
        let advertiserId = "";
        let isVideo = false;
        let sizes = [];
        
        if (onDeufaultRoute) {
          let dimensions = routeName.split('/default/')[1].split('x');
          let dcmSize = await fetchSizeService(dimensions[0], dimensions[1])
          sizes = [dcmSize.id];
          if (campaigns.selectedId.length > 0) {
            advertiserId = campaigns.byId[campaigns.selectedId].advertiserId;
          }

          if (dimensions[0] === "0" && dimensions[1] === "0") {
            isVideo = true
            sizes = []
          }

          stableDispatch(
            fetchAssignedCreatives("all", isVideo ? ["INSTREAM_VIDEO"] : creativeTypes, sizes, advertiserId)
          );
        }
      }
      getSizeId();
    }, [campaigns.byId, campaigns.selectedId, creativeTypes, stableDispatch, routeName])

    const handleChangePage = (event, newPage) => {
      setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
      setRowsPerPage(+event.target.value);
      setPage(0);
    };

    const handleSelectCreative = (creative) => {
      const selectedIndex = selectedCreatives.indexOf(creative);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedCreatives, creative);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedCreatives.slice(1));
      } else if (selectedIndex === selectedCreatives.length - 1) {
        newSelected = newSelected.concat(selectedCreatives.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedCreatives.slice(0, selectedIndex),
          selectedCreatives.slice(selectedIndex + 1)
        );
      }

      setSelectedCreatives(newSelected);
    };

    // const isSelected = (creative) => selectedCreatives.indexOf(creative) !== -1;
    const isSelected = (creative) => {
      let foundCreative = selectedCreatives.find((c) => c.id === creative.id);

      if (foundCreative) {
        return true;
      } else {
        return false;
      }
    };

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        const newSelectedCreatives = dcmCreatives;
        setSelectedCreatives(newSelectedCreatives);
        return;
      }
      setSelectedCreatives([]);
    };

    useImperativeHandle(ref, () => ({
      validate() {
        if (selectedCreatives.length === 0) {
          return {
            hasErrors: true,
            message: "You need to add at least one creative."
          };
        }

        return {
          hasErrors: false,
          message: ""
        };
      }
    }));

    return (
      <Grid container>
        <Grid item xs={12} className={classes.grid}>
          <Paper className={classes.paper}>
            <TableToolbar
              numSelected={selectedCreatives.length}
              onSearch={(e) => setSearchText(e.target.value)}
              onClear={() => setSearchText("")}
              searchTerm={searchText}
            />
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      onClick={(event) => handleSelectAllClick(event)}
                      indeterminate={
                        selectedCreatives.length > 0 &&
                        selectedCreatives < dcmCreatives.length
                      }
                      checked={
                        selectedCreatives.length > 0 &&
                        selectedCreatives.length === dcmCreatives.length
                      }
                    />
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Dimensions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress
                        style={{
                          position: "relative",
                          margin: "2vh 0 2vh 0"
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  dcmCreatives
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((creative, index) => {
                      return (
                        <TableRow key={`creative-list-${index}`}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected(creative)}
                              onClick={(event) =>
                                handleSelectCreative(creative)
                              }
                            />
                          </TableCell>
                          <TableCell>{creative.id}</TableCell>
                          <TableCell>{creative.name}</TableCell>
                          <TableCell align="right">{`${
                            creative.size.width === 0 &&
                            creative.type === "TRACKING_TEXT"
                              ? 1
                              : creative.size.width
                          } x ${
                            creative.size.height === 0 &&
                            creative.type === "TRACKING_TEXT"
                              ? 1
                              : creative.size.height
                          }`}</TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={dcmCreatives.length}
              rowsPerPage={rowsPerPage}
              page={page}
              backIconButtonProps={{
                "aria-label": "previous page"
              }}
              nextIconButtonProps={{
                "aria-label": "next page"
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Paper>
        </Grid>
      </Grid>
    );
  }
);
